import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import { useCallback, useState } from 'react';
import { AddressSearchSuggest } from './search-suggest';
import { captureException } from '~/lib/metrics';
import { AddressLookup, AddressSearchOverview } from './types';
import { addressBreakdown, addressRetrieve, addressSearch } from './address-search';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';

type AddressSearchProps = {
  apiPath: string;
  onSelected: (address: AddressLookup) => void;
  type: 'Origin' | 'Destination';
  countryIsoCode?: string;
  postcode?: string;
  translationNamespace: string;
  errors: Record<string, string>;
  optional?: boolean;
};

const MAX_RESULTS = 50;

export default function AddressSearchCore({
  apiPath,
  countryIsoCode,
  postcode,
  type,
  onSelected,
  translationNamespace,
  errors,
  optional
}: AddressSearchProps) {
  const { track, session } = useAnalytics();
  const { tenant } = useTenant();
  const [results, setResults] = useState<AddressSearchOverview[]>([]);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const onBreakdown = useCallback(
    async (query: string, address: AddressSearchOverview) => {
      if (!address?.identifier) {
        return;
      }
      try {
        setLoading(true);
        const result = await addressBreakdown(apiPath, tenant, session, countryIsoCode, query, address.identifier);
        setResults(result);
        track({
          type: 'address_search_breakdown',
          payload: {
            ...GenerateBaseAnalyticsPayload([]),
            identifier: address.identifier,
            count: result.length || 0,
            type
          }
        });
      } catch (e) {
        captureException(e, 'Error in AddressSearch onBreakdown', {
          section: 'checkout'
        });
      } finally {
        setLoading(false);
      }
    },
    [countryIsoCode, tenant, track, type, session, apiPath]
  );

  const onRetrieve = useCallback(
    async (address: AddressSearchOverview) => {
      if (!address?.identifier) {
        return;
      }
      try {
        setLoading(true);
        const result = await addressRetrieve(apiPath, tenant, session, countryIsoCode, address.identifier);
        setResults([]);
        onSelected(result);
      } catch (e) {
        captureException(e, 'Error in AddressSearch onRetrieve', {
          section: 'checkout'
        });
      } finally {
        setLoading(false);
      }
    },
    [onSelected, countryIsoCode, tenant, session, apiPath]
  );

  const onClick = useCallback(
    async (query: string, address: AddressSearchOverview) => {
      if (address.hasBreakdown) {
        await onBreakdown(query, address);
      } else {
        await onRetrieve(address);
      }
    },
    [onBreakdown, onRetrieve]
  );

  const onSearch = useCallback(
    async (query: string, autoSelectSingleResult: boolean = true) => {
      setResults([]);

      if (!query || query.length <= 2) {
        return;
      }

      try {
        setLoading(true);
        setSkip(0);
        const result = await addressSearch(apiPath, tenant, session, countryIsoCode, query, 0, MAX_RESULTS);
        setResults(result);
        setHasMore(result.length === MAX_RESULTS);
        track({
          type: 'address_search',
          payload: {
            ...GenerateBaseAnalyticsPayload([]),
            query,
            count: result.length,
            type
          }
        });
        if (autoSelectSingleResult && result.length === 1) {
          onClick(query, result[0]);
          return;
        }
      } catch (e) {
        captureException(e, 'Error in AddressSearch onSearch', {
          section: 'checkout'
        });
      } finally {
        setLoading(false);
      }
    },
    [onClick, countryIsoCode, tenant, track, type, session, apiPath]
  );

  const onLoadMore = useCallback(
    async (query: string) => {
      const result = await addressSearch(
        apiPath,
        tenant,
        session,
        countryIsoCode,
        query,
        skip + MAX_RESULTS,
        MAX_RESULTS
      );
      setResults((prev) => [...prev, ...result]);
      setSkip((prevSkip) => prevSkip + MAX_RESULTS);
      setHasMore(result.length === MAX_RESULTS);
    },
    [countryIsoCode, tenant, session, skip, apiPath]
  );

  return (
    <AddressSearchSuggest
      initial={postcode}
      optional={optional}
      type={type}
      results={results}
      onSearch={onSearch}
      onClick={onClick}
      loading={loading}
      errors={errors}
      onLoadMore={onLoadMore}
      hasMore={hasMore}
      translationNamespace={translationNamespace}
    />
  );
}
